import React from 'react';
import { Vehicule } from '../../../../shared/types/game';
import { VehiculeConfig, FonctionArmement, Competence } from '../../../../shared/types/config';

interface VehiculeCompetencesSectionProps {
  vehicule: Vehicule;
  vehiculeConfigDetails: VehiculeConfig | null;
  fonctionsArmement: FonctionArmement[];
  competences: Competence[];
  previewFonctionArmement: number | null;
}

/**
 * Bloc « Compétences requises » + bloc vide de fallback extrait de
 * VehiculeDetailsModal.tsx.
 */
const VehiculeCompetencesSection: React.FC<VehiculeCompetencesSectionProps> = ({
  vehicule,
  vehiculeConfigDetails,
  fonctionsArmement,
  competences,
  previewFonctionArmement,
}) => {
  const fonctionIdPourAffichage = previewFonctionArmement ?? vehicule.id_fonction_armement;
  const fonction = fonctionIdPourAffichage
    ? fonctionsArmement.find((f) => f.id === fonctionIdPourAffichage)
    : null;

  if (fonction && fonction.id_competences && fonction.id_competences.length > 0) {
    const competencesWithOrder = fonction.id_competences
      .map((compFonction, index) => {
        const competence = competences.find((c) => c.id === compFonction.id);
        if (!competence) return null;
        return {
          id: compFonction.id,
          competence,
          ordre: compFonction.ordre || 999,
          isOptionnel: compFonction.optionnel === true,
          isRef: compFonction.ref === true,
          uniqueKey: `${compFonction.id}-${index}-${compFonction.ordre}`,
        };
      })
      .filter(
        (item): item is {
          id: number;
          competence: Competence;
          ordre: number;
          isOptionnel: boolean;
          isRef: boolean;
          uniqueKey: string;
        } => item !== null
      )
      .sort((a, b) => (a.ordre !== b.ordre ? a.ordre - b.ordre : 0));

    if (competencesWithOrder.length > 0) {
      return (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            {previewFonctionArmement ? `Armement - ${fonction.nom}` : 'Compétences requises'} (
            {competencesWithOrder.length})
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {competencesWithOrder.map(({ uniqueKey, competence, isOptionnel, isRef }) => {
              const hasBoth = isOptionnel && isRef;
              return (
                <div
                  key={uniqueKey}
                  className={`bg-gray-800 rounded-lg p-4 border-2 ${
                    hasBoth
                      ? 'border-l-4 border-l-green-500/70 border-r border-r-yellow-500/50 border-t border-t-yellow-500/50 border-b border-b-yellow-500/50'
                      : isRef
                        ? 'border-green-500/20'
                        : isOptionnel
                          ? 'border-yellow-500/20'
                          : 'border-blue-500/20'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`font-medium ${
                        hasBoth
                          ? 'text-green-300'
                          : isRef
                            ? 'text-green-400'
                            : isOptionnel
                              ? 'text-yellow-400'
                              : 'text-blue-400'
                      }`}
                    >
                      {competence.nom}
                    </span>
                    <div className="flex items-center gap-2">
                      {isOptionnel && (
                        <span className="text-xs text-yellow-400 bg-yellow-500/20 px-2 py-1 rounded">
                          Optionnel
                        </span>
                      )}
                      {isRef && (
                        <span className="text-xs text-green-400 bg-green-500/20 px-2 py-1 rounded">
                          Ref
                        </span>
                      )}
                    </div>
                  </div>
                  {competence.is_specialite && (
                    <span className="text-xs text-purple-400 mt-2 inline-block">Spécialité</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }
  }

  // Fallback : bloc vide quand aucune fonction n'est sélectionnée et aucune config
  const competencesAAfficher = fonction?.id_competences
    ? fonction.id_competences.map((c) => c.id)
    : [];
  const aDesFonctionsConfig =
    (!!vehiculeConfigDetails?.id_fonctions_armement &&
      vehiculeConfigDetails.id_fonctions_armement.length > 0) ||
    (!!vehiculeConfigDetails?.id_competences_armement &&
      vehiculeConfigDetails.id_competences_armement.length > 0) ||
    !!vehiculeConfigDetails?.fonction_depart?.trim();
  const showEmpty =
    !fonction &&
    competencesAAfficher.length === 0 &&
    !vehicule.id_fonction_armement &&
    !aDesFonctionsConfig;

  if (showEmpty) {
    return (
      <div className="mb-6">
        <div className="bg-gray-800 rounded-lg p-4 text-center text-gray-400">
          <p>Aucune fonction d'armement ou compétence assignée</p>
        </div>
      </div>
    );
  }

  return null;
};

export default VehiculeCompetencesSection;
